import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Star, Quote } from "lucide-react";
import { values, stats, clientLogos, reviews, company } from "../mock";
import Reveal from "../components/Reveal";
import CountUp from "../components/CountUp";
import Seo from "../components/Seo";

const About = () => {
  return (
    <div className="overflow-hidden">
      <Seo
        title="About iClean NW | North West Cleaners"
        description="Learn about iClean NW, established to deliver hassle-free, high-standard commercial cleaning across Manchester and the North West with a vetted, trained and fully insured team."
        path="/about"
      />
      <section className="pt-32 lg:pt-40 pb-4">
        <div className="max-w-6xl mx-auto px-5 lg:px-6">
          <span className="text-[#ea731d] font-semibold tracking-[0.2em] text-xs uppercase">About iClean</span>
          <h1 className="mt-3 text-4xl lg:text-6xl font-bold text-[#1a1714] leading-[1.05] max-w-4xl">
            Built on a simple idea: cleaning should be hassle-free
          </h1>
          <p className="mt-5 text-lg text-neutral-600 max-w-2xl">
            iClean was established with the vision to create a hassle-free experience for its clients across {company.area}.
          </p>
        </div>
      </section>

      {/* STORY */}
      <section className="py-14 lg:py-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-6 grid lg:grid-cols-2 gap-12 items-center">
          <Reveal className="relative">
            <div className="rounded-[28px] overflow-hidden soft-shadow">
              <img src="/generated/office.jpg?v=4" alt="iClean at work" className="w-full h-[440px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#1a1714]">Our mission</h2>
            <p className="mt-4 text-neutral-600 leading-relaxed">
              Our mission is to deliver and maintain the highest standards in cleaning service, with total commitment to customer satisfaction through our responsiveness.
            </p>
            <p className="mt-4 text-neutral-600 leading-relaxed">
              The quality of our work, and the personal attention we devote to each client and contracted assignment, gives you total peace of mind that your service is in the right hands.
            </p>
            <ul className="mt-6 space-y-3">
              {["Exceeding the expectations of clients and their visitors", "Continuous improvement in quality, service & productivity", "Vetted, trained and reliable staff on every job"].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-[#ea731d]/15 text-[#ea731d] flex items-center justify-center"><Check size={13} strokeWidth={3} /></span>
                  <span className="text-neutral-700">{t}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* STATS */}
      <section className="py-6">
        <div className="max-w-6xl mx-auto px-5 lg:px-6">
          <div className="rounded-[28px] bg-white border border-neutral-100 px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8 soft-shadow">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 80} className="text-center">
                <div className="font-display text-4xl lg:text-5xl font-bold text-[#ea731d]"><CountUp value={s.value} /></div>
                <div className="mt-2 text-sm text-neutral-500">{s.label}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-5 lg:px-6">
          <Reveal className="max-w-2xl">
            <span className="text-[#ea731d] font-semibold tracking-widest text-xs uppercase">What we stand for</span>
            <h2 className="mt-3 text-3xl lg:text-5xl font-bold text-[#1a1714]">Our values</h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <Reveal key={v.title} delay={i * 90}>
                  <div className="bg-white rounded-[22px] p-7 h-full border border-neutral-100 hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                    <div className="w-12 h-12 rounded-2xl bg-[#ea731d]/12 text-[#ea731d] flex items-center justify-center"><Icon size={24} /></div>
                    <h3 className="mt-5 font-bold text-[#1a1714]">{v.title}</h3>
                    <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{v.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CLIENTS */}
      <section className="py-6 pb-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-6">
          <Reveal className="text-center max-w-xl mx-auto">
            <h2 className="text-2xl lg:text-3xl font-bold text-[#1a1714]">Some of the clients we work with</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {clientLogos.map((c, i) => (
              <Reveal key={c.name} delay={(i % 5) * 60}>
                <div
                  className="rounded-2xl border border-neutral-200/70 h-24 flex items-center justify-center p-4 overflow-hidden shadow-sm hover:shadow-md transition-all"
                  style={{ background: c.bg }}
                  title={c.name}
                >
                  <img src={c.src} alt={c.name} className="max-h-full max-w-full object-contain" loading="lazy" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="pb-24">
        <div className="max-w-5xl mx-auto px-5 lg:px-6">
          <Reveal className="rounded-[32px] bg-[#ea731d] px-8 py-14 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white">Let’s work together</h2>
            <p className="mt-3 text-orange-50">Experience the iClean difference for yourself.</p>
            <Link to="/contact" className="mt-7 inline-flex items-center gap-2 bg-white text-[#ea731d] font-bold px-8 py-4 rounded-full hover:-translate-y-0.5 transition-transform">
              Get in touch <ArrowRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default About;
